import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { DP } from '../../../drupal/blockDefinitions'
import { mediaContentSrc, mediaContentSrcSet, mediaFullSrc, mediaFullSrcSet, mediaOrginalSrc, mediaOrginalSrcSet } from '../../../drupal/utils/imageSizes'
import { parseDrupalImageUrls } from '../../../drupal/utils/modernImage'
import { dummyData, randomCaption, randomTitle, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import type { ModernBlock } from '../../modernBlocks.state'
import { mediaBlockClasses, MediaBlockLayout } from './mediaBlock.layout'
import type { MediaBlockMedia, MediaBlockProps, MediaOriginalSizeProps } from './mediaBlock.state'
import { VideoLayout } from './mediaVideo.layout'

export const mediaBlock: ModernBlock<MediaBlockProps, any, any, DP.MediaImage | DP.MediaVideo> = {
  kind: 'mediaBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <MediaBlockLayout.Inner amountNested={amountNested!}>
        <MediaBlockLayout.Content title={blockProps.title} intro={blockProps.intro} label={blockProps.label} />
        {blockProps.items.map((item, itemIndex) =>
          item.type == 'image' ? (
            <MediaBlockLayout.Visual item={item} key={`${blockIndex}-${itemIndex.toString()}`} id={itemIndex.toString()}>
              <MediaBlockLayout.Image
                displayOption={item.displayOption}
                originalSize={item.originalSize}
                image={item.image}
              />
            </MediaBlockLayout.Visual>
          ) : item.type == 'video' ? (
            <MediaBlockLayout.Visual item={item} key={`${blockIndex}-${itemIndex.toString()}`} id={itemIndex.toString()}>
              <VideoLayout
                mediaBlock={item}
                rootState={rootState}
                setState={setRootState}
              />
            </MediaBlockLayout.Visual>
          ) : (
            <></>
          )
        )}

        <MediaBlockLayout.Footer
          showPrimary={blockProps.showPrimary}
          showSecondary={blockProps.showSecondary}
          primaryAction={blockProps.primaryAction!}
          secondaryAction={blockProps.secondaryAction!}
        />
      </MediaBlockLayout.Inner>
    )),
  adapters: {
    drupal: (dpBlock: DP.MediaImage | DP.MediaVideo, blockIndex: number) => {
      const targetId = dpBlock.target.type[0].target_id
      if (targetId != 'media') return undefined
      return {
        label: dpBlock.target.field_label[0]?.value,
        title: dpBlock.target.field_title[0]?.value,
        intro: dpBlock.target.field_introduction[0]?.value,
        excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu?.[0]?.value,
        showPrimary: dpBlock.target.field_links[0] ? true : false,
        showSecondary: dpBlock.target.field_links[1] ? true : false,
        primaryAction: linkWrapper({
          title: dpBlock.target?.field_links[0]?.title!,
          url: dpBlock.target?.field_links[0]?.processed_url!,
          anchor: dpBlock.target?.field_links[0]?.options?.attributes?.id!,
        }),
        secondaryAction: linkWrapper({
          title: dpBlock.target?.field_links[1]?.title!,
          url: dpBlock.target?.field_links[1]?.processed_url!,
          anchor: dpBlock.target?.field_links[1]?.options?.attributes?.id!,
        }),
        items: dpBlock.target.field_media_item?.map<MediaBlockMedia>((item: any) => {
          const displayOption = item.target.field_image_option[0]?.value.includes('full')
            ? ('full-width' as 'full-width')
            : ('content-width' as 'content-width')
          const isImage = item.target.field_media[0]?.target.bundle[0]?.target_id == 'image'
          const isVideo = item.target.field_media[0]?.target.bundle[0]?.target_id == 'video'
          const fieldMediaImage = item.target.field_media[0].target.field_media_image
            ? item.target.field_media[0].target.field_media_image[0]
            : undefined
          const fieldMediaSource = item.target.field_media[0]?.target.field_source[0]
            ? item.target.field_media[0]?.target.field_source
            : undefined
          const originalSizeAttrs: MediaOriginalSizeProps = isImage ? {
            showinOriginalSize: item.target.field_original_size[0]?.value,
            width: item.target.field_media[0]?.target.field_media_image[0]?.width,
            height: item.target.field_media[0]?.target.field_media_image[0]?.height,
          } : {
            showinOriginalSize: false
          }
          const dpImages = isImage
            ? parseDrupalImageUrls(
                fieldMediaImage,
                fieldMediaSource,
                originalSizeAttrs.showinOriginalSize == true ? mediaOrginalSrcSet : displayOption == 'content-width' ? mediaContentSrcSet : mediaFullSrcSet,
                originalSizeAttrs.showinOriginalSize == true ? mediaOrginalSrc : displayOption == 'content-width' ? mediaContentSrc : mediaFullSrc
              )?.image
            : undefined
          return {
            type: isImage ? ('image' as 'image') : ('video' as 'video'),
            displayOption: displayOption,
            originalSize: originalSizeAttrs,
            image: isImage ? dpImages : undefined,
            url: isVideo
              ? (item.target.field_media[0]?.target as any).field_media_video_embed_field[0]?.value
              : undefined,
            label: isVideo
              ? item.target.field_media[0]?.target_label ? item.target.field_media[0]?.target_label
              : undefined : undefined,
            caption: item.target.field_image_caption?.length > 0 ? item.target.field_image_caption[0]?.value : '',
            source: fieldMediaSource ? fieldMediaSource[0]?.value : '',
          }
        }),
        anchorTitle: dpBlock.target.field_title[0]
          ? slugify(dpBlock.target.field_title[0]?.value)
          : `${targetId}-title-${blockIndex}`,
      }
    },
  },
  sampleExample: [
    {
      anchorTitle: 'sample-media-1',
      label: randomWords(),
      title: randomTitle(),
      intro: dummyData.intro,
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      items: [
        {
          type: 'image',
          image: dummyData.image,
          caption: randomCaption(),
          source: randomWords(),
          displayOption: 'content-width',
          originalSize: {
            showinOriginalSize: false
          }
        },
        {
          type: 'image',
          image: dummyData.image,
          displayOption: 'full-width',
          originalSize: {
            showinOriginalSize: false
          }
        },
      ],
    },
    {
      anchorTitle: 'sample-media-2',
      label: randomWords(),
      title: randomTitle(),
      intro: dummyData.intro,
      showPrimary: false,
      showSecondary: false,
      items: [
        {
          type: 'video',
          caption: randomCaption(),
          source: randomWords(),
          url: dummyData.video,
          displayOption: 'content-width',
          label: randomTitle()
        },
        {
          type: 'video',
          url: dummyData.video,
          displayOption: 'full-width',
        },
      ],
    },
  ],
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(mediaBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState,
      }
    },
  },
  blockClassName: (props) => getClassNameModern(mediaBlockClasses, 'block', 'mb-6'),
}
